import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { fetchTranslations } from '@chilipiper/service'
import { store } from '@chilipiper/utils'
import { captureException } from '@chilipiper/sentry'
import { defaultLocale, getChiliLocale, getLocale, Locale, locales } from './locales'
import { defaultTranslation } from './default-translation'

interface LocaleContext {
  locale: Locale
  setLocale: (locale: Locale) => void
  translations: Record<string, string>
}

const localeContext = createContext<LocaleContext | undefined>(undefined)

interface LocaleProviderProps {
  children: ReactNode
  enabledLanguages?: Locale[]
  fetchLocale: (locale: Locale) => Promise<Record<string, string> | undefined>
}

export const LocaleProvider = ({
  children,
  enabledLanguages = [],
  fetchLocale,
}: LocaleProviderProps) => {
  const [translations, setTranslations] = useState<Record<Locale, Record<string, string>>>(
    {} as Record<Locale, Record<string, string>>
  )
  const [locale, setLocale] = useState<Locale>(defaultLocale)

  useEffect(() => {
    const forcedLocale = new URLSearchParams(window.location.search).get('locale')
    if (forcedLocale) {
      store.session.set('locale', forcedLocale) // if locale was forced persist it for other pages
    }
    const realLocale = (forcedLocale ?? store.session.get('locale') ?? getLocale()) as Locale
    const pickedLocale =
      [realLocale, navigator.language, ...navigator.languages]
        .map(getChiliLocale)
        .find(language => {
          return language && enabledLanguages?.includes(language)
        }) ?? defaultLocale
    setLocale(pickedLocale)
  }, [enabledLanguages])

  useEffect(() => {
    moment.locale(locale)

    const isRtl = (locale: Locale) => 'rtl' in locales[locale]
    document.documentElement.setAttribute('dir', isRtl(locale) ? 'rtl' : 'ltr')
    document.documentElement.setAttribute('lang', locales[locale].lang)

    if (!translations[locale]) {
      fetchLocale(locale)
        .then(fetchedTranslations => {
          setTranslations(prevTranslations => ({
            ...prevTranslations,
            [locale]: fetchedTranslations,
          }))
        })
        .catch(e => console.error(`Failed to fetch locale: ${locale}, reason: ${e}`))
    }
  }, [locale])

  const value = useMemo(
    () => ({
      locale,
      translations: translations[locale] ?? {},
      setLocale: (locale: Locale) => {
        store.session.set('locale', locale)
        setLocale(locale)
      },
    }),
    [locale, translations]
  )
  return <localeContext.Provider value={value}>{children}</localeContext.Provider>
}

export const useLocale = () => {
  const value = useContext(localeContext)
  if (!value) {
    throw new Error('useLocale must be used inside of a LocaleProvider')
  }

  return value.locale
}

export const useTranslations = (keys: string[]) => {
  const value = useContext(localeContext)
  if (!value) {
    throw new Error('useLocale must be used inside of a LocaleProvider')
  }

  return keys.map(key => {
    return value.translations[key] ?? ''
  })
}

export const defaultFetchLocale = async (
  locale: Locale
): Promise<Record<string, string> | undefined> => {
  try {
    if (locale !== defaultLocale) {
      try {
        import(`./moment-locales/${locale}.ts`)
      } catch (e) {
        console.error(`Couldn't fetch moment locale ${locale}`, e)
      }
    } else {
      return defaultTranslation.concierge
    }
    return (await fetchTranslations(locale)).concierge
  } catch (e) {
    console.error(`Couldn't fetch locale ${locale}`, e)
    captureException(e)
    return defaultTranslation.concierge
  }
}
